import * as actionTypes from 'js/constants/actionTypes';
import { SCHEMA_TYPE_AI_TOOL_PACKAGES } from 'js/schemas/aiTool';

export interface AIToolset {
  id: number;
  org_id: number;
  owner_id?: number;
  key?: string;
  name: string;
  display_name?: string; // Deprecated alias for name
  description?: string;
  status: 'init' | 'paused' | 'active';
  // MCP server key for direct access
  mcp_server_key?: string;
  // Manifest stored directly (versionless)
  manifest?: Record<string, any>;
  manifest_hash?: string;
  tools_hash?: string;
  // Compatibility and provenance
  compatibility?: Record<string, any>;
  provenance?: Record<string, any>;
  copied_from_id?: number;
  tags?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface AIToolsetsState {
  byId: Record<number, AIToolset>;
  allIds: number[];
  loading: boolean;
  error: string | null;
  lastUpdated: number | null;
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
  } | null;
}

const initialState: AIToolsetsState = {
  byId: {},
  allIds: [],
  loading: false,
  error: null,
  lastUpdated: null,
  pagination: null,
};

export default function aiToolsetsReducer(
  state: AIToolsetsState = initialState,
  action: any,
): AIToolsetsState {
  switch (action.type) {
    case actionTypes.GET_AI_TOOLSETS_REQUEST:
    case actionTypes.GET_AI_TOOLSET_REQUEST:
    case actionTypes.CREATE_AI_TOOL_PACKAGE_REQUEST: // Toolsets use same action types as packages
    case actionTypes.UPDATE_AI_TOOLSET_REQUEST:
    case actionTypes.ACTIVATE_AI_TOOLSET_REQUEST:
    case actionTypes.PAUSE_AI_TOOLSET_REQUEST:
    case actionTypes.DISABLE_AI_TOOLSET_REQUEST:
    case actionTypes.ENABLE_AI_TOOLSET_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.GET_AI_TOOLSETS_SUCCESS: {
      const toolsets = action?.entities?.[SCHEMA_TYPE_AI_TOOL_PACKAGES] || {};
      const ids = action?.result || [];
      const shouldAppend = action.requestInfo?.append;
      const mergedIds = shouldAppend
        ? [...state.allIds, ...ids.filter((id) => !state.allIds.includes(id))]
        : ids;

      // Extract pagination metadata
      const rspHeaders = action.rspHeaders || {};
      const totalItems = Number(rspHeaders['X-Total-Count'] || ids.length);
      const currentPage = Number(rspHeaders['X-Current-Page'] || action.requestInfo?.page || 1);
      const pageSize = Number(action.requestInfo?.limit || 50);
      const totalPages = Number(
        rspHeaders['X-Total-Page-Count'] || Math.ceil(totalItems / pageSize) || 1,
      );

      return {
        ...state,
        byId: { ...state.byId, ...toolsets },
        allIds: mergedIds,
        loading: false,
        lastUpdated: Date.now(),
        pagination: {
          totalItems,
          totalPages,
          currentPage,
          pageSize,
        },
      };
    }

    case actionTypes.GET_AI_TOOLSET_SUCCESS:
    case actionTypes.CREATE_AI_TOOL_PACKAGE_SUCCESS: // Toolsets use same action types as packages
    case actionTypes.UPDATE_AI_TOOLSET_SUCCESS:
    case actionTypes.ACTIVATE_AI_TOOLSET_SUCCESS:
    case actionTypes.PAUSE_AI_TOOLSET_SUCCESS:
    case actionTypes.DISABLE_AI_TOOLSET_SUCCESS:
    case actionTypes.ENABLE_AI_TOOLSET_SUCCESS: {
      const toolset = action.response?.entities?.[SCHEMA_TYPE_AI_TOOL_PACKAGES] || {};
      const toolsetId = action.response?.result || action.requestInfo?.id;

      if (!toolsetId) return state;

      return {
        ...state,
        byId: { ...state.byId, ...toolset },
        allIds: state.allIds.includes(toolsetId) ? state.allIds : [...state.allIds, toolsetId],
        loading: false,
        lastUpdated: Date.now(),
      };
    }

    case actionTypes.GET_AI_TOOLSETS_FAILURE:
    case actionTypes.GET_AI_TOOLSET_FAILURE:
    case actionTypes.CREATE_AI_TOOL_PACKAGE_FAILURE:
    case actionTypes.UPDATE_AI_TOOLSET_FAILURE:
    case actionTypes.ACTIVATE_AI_TOOLSET_FAILURE:
    case actionTypes.PAUSE_AI_TOOLSET_FAILURE:
    case actionTypes.DISABLE_AI_TOOLSET_FAILURE:
    case actionTypes.ENABLE_AI_TOOLSET_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error?.message || 'An error occurred',
      };

    default:
      return state;
  }
}
